/* ============================================================
   storage — the preview pane.

   Clicking a file should tell you what it is, not put it in your
   downloads folder. What that means depends on the file:

     image                   the browser renders it from an inline
                             URL — no bytes through this app
     video / audio           the same URL, in the player (MediaPlayer),
                             which owns its own controls
     pdf                     the browser's own viewer, in a frame
     text and code           fetched as text, and editable in
                             place when it is yours
     anything else           what it is, how big, and a way to
                             download it

   The bytes for the first three come straight from nginx with a
   short-lived token in the URL, because a <video> cannot carry an
   Authorization header.
   ============================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon, fileIconName, fmtBytes, fmtDate } from './lib';
import { api } from './api';
import MediaPlayer, { savedPosition } from './MediaPlayer';
import { useStreams, qualitiesFrom, streamNote } from './streams';
import { useDirectSrc } from './direct';

const TEXT_EXT = new Set([
  'txt', 'md', 'markdown', 'log', 'csv', 'tsv', 'json', 'xml', 'yml', 'yaml',
  'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash', 'zsh', 'py', 'js', 'jsx',
  'ts', 'tsx', 'css', 'scss', 'html', 'htm', 'svg', 'c', 'h', 'cpp', 'hpp',
  'java', 'go', 'rs', 'rb', 'php', 'sql', 'lua', 'pl', 'r', 'swift', 'kt',
  'gradle', 'properties', 'gitignore', 'patch', 'diff', 'srt', 'vtt', 'tex',
]);

export function previewKind(file) {
  const mime = (file?.mime_type || '').toLowerCase();
  const name = (file?.original_name || '').toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : '';
  if (mime.startsWith('image/') && mime !== 'image/svg+xml') return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('text/') || TEXT_EXT.has(ext)
      || ['application/json', 'application/xml', 'application/javascript'].includes(mime)) {
    return 'text';
  }
  return 'none';
}

export default function Preview({
  token, file, me, canEdit, onClose, onDownload, onChanged, toast,
  onPrev, onNext, hasPrev, hasNext, onOpenTab, startAt, onPosition, trim, directBase,
}) {
  const kind = previewKind(file);
  const [text, setText] = useState(null);
  const [draft, setDraft] = useState(null);      // non-null while editing
  const [state, setState] = useState('idle');    // idle | loading | error | saving
  const [meta, setMeta] = useState({});
  const [error, setError] = useState('');
  const [mediaFailed, setMediaFailed] = useState(false);
  const boxRef = useRef(null);

  // The hash rides along so a video trimmed in place (same id, new bytes)
  // is fetched again rather than played from the browser's cache.
  const normalSrc = api.inlineUrl(token, file.id, file.sha256);
  // On a drive with an agent this device can reach, the bytes come from that
  // box directly (direct.js) — for a video, its lighter copies too.
  const media = useDirectSrc(token, file, directBase, normalSrc, ['video', 'audio', 'image', 'pdf'].includes(kind));
  const src = media.src || normalSrc;
  // Heavy videos have lighter copies; the player offers them, and Auto picks.
  const streams = useStreams(kind === 'video' ? () => api.fileStreams(token, file.id) : null,
    kind === 'video' ? `${file.id}:${file.sha256 || ''}` : null);
  const qualities = streams ? qualitiesFrom(streams,
    (q) => (media.direct && streams.direct?.[q]) || api.streamCopyUrl(token, file.id, q), src) : undefined;

  useEffect(() => { setDraft(null); setError(''); setMediaFailed(false); }, [file.id]);

  useEffect(() => {
    if (kind !== 'text') { setText(null); return undefined; }
    const ctrl = new AbortController();
    setState('loading'); setError('');
    api.readText(token, file.id, ctrl.signal)
      .then((d) => { setText(d.text); setMeta(d); setState('idle'); })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.message || 'Could not read this file'); setState('error');
      });
    return () => ctrl.abort();
  }, [token, file.id, kind]);

  const save = useCallback(async () => {
    setState('saving');
    try {
      await api.writeText(token, file.id, draft);
      setText(draft); setDraft(null); setState('idle');
      toast?.('ok', 'Saved', file.original_name);
      onChanged?.();
    } catch (e) {
      setState('idle');
      toast?.('err', 'Could not save', e.message);
    }
  }, [token, file.id, file.original_name, draft, toast, onChanged]);

  // Arrows step through the folder, but not while someone is typing into the
  // editor — there, the arrows belong to the text.
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's' && draft !== null) {
          e.preventDefault(); save();
        }
        return;
      }
      if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); onNext(); }
      else if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); onPrev(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [draft, save, onNext, onPrev, hasNext, hasPrev]);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = 0; }, [file.id]);

  const body = () => {
    if (mediaFailed || kind === 'none') {
      return (
        <div className="st-pv-empty">
          <Icon name={fileIconName(file.mime_type, file.original_name)} size={44}/>
          <p className="meta">
            {mediaFailed
              ? 'This browser could not play this file.'
              : 'No preview for this kind of file.'}
          </p>
          <button type="button" className="btn btn--sm" onClick={() => onDownload(file)}>
            <Icon name="download" size={14}/> Download
          </button>
        </div>
      );
    }
    if ((kind === 'video' || kind === 'audio' || kind === 'image' || kind === 'pdf') && !media.src) {
      return <div className="st-pv-empty"><span className="skeleton st-viewer-skel"/></div>;
    }
    if (kind === 'image') {
      return (
        <a className="st-pv-media" href={src} target="_blank" rel="noreferrer"
           title="Open full size in a new tab">
          <img src={src} alt={file.original_name} onError={() => setMediaFailed(true)}/>
        </a>
      );
    }
    if (kind === 'video' || kind === 'audio') {
      // Stepping to the next clip is part of watching a folder of them, so
      // the player gets the same prev/next the header has.
      return (
        <MediaPlayer
          key={`${file.id}:${file.sha256 || ''}`}
          src={src}
          name={file.original_name}
          kind={kind}
          mediaKey={file.id}
          token={token}
          startAt={startAt}
          onPosition={onPosition}
          onPrev={onPrev} onNext={onNext} hasPrev={hasPrev} hasNext={hasNext}
          onError={() => setMediaFailed(true)}
          onDownload={() => onDownload(file)}
          onOpenTab={onOpenTab}
          qualities={qualities}
          qualityNote={streamNote(streams)}
          trim={kind === 'video' ? trim : undefined}
          file={file}
        />
      );
    }
    if (kind === 'pdf') {
      return <iframe className="st-pv-frame" src={src} title={file.original_name}/>;
    }
    // text
    if (state === 'loading') {
      return (
        <div className="st-pv-text">
          {[88, 72, 94, 60, 80, 55, 90].map((w, i) => (
            <span key={i} className="skeleton st-pv-skel" style={{ width: `${w}%` }}/>
          ))}
        </div>
      );
    }
    if (state === 'error') {
      return (
        <div className="st-pv-empty">
          <Icon name="warn" size={36}/>
          <p className="meta">{error}</p>
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => onDownload(file)}>
            <Icon name="download" size={14}/> Download instead
          </button>
        </div>
      );
    }
    if (draft !== null) {
      return (
        <textarea className="st-pv-editor" value={draft} spellCheck="false"
                  onChange={(e) => setDraft(e.target.value)}
                  aria-label={`Contents of ${file.original_name}`}/>
      );
    }
    return (
      <>
        {meta.truncated && (
          <p className="st-pv-note meta">
            Showing the first 1 MB. Download the file to see the rest.
          </p>
        )}
        <pre className="st-pv-text">{text}</pre>
      </>
    );
  };

  const editable = kind === 'text' && canEdit && meta.editable && state !== 'error';

  return (
    <section className="st-panel st-pv" aria-label={`Preview of ${file.original_name}`}>
      <header className="st-panel-head st-pv-head">
        <div className="st-pv-title">
          <Icon name={fileIconName(file.mime_type, file.original_name)} size={15} className="st-icon-file"/>
          <span className="st-pv-name" title={file.original_name}>{file.original_name}</span>
        </div>
        <div className="st-pv-nav">
          <button type="button" className="iconbtn" aria-label="Previous file"
                  disabled={!hasPrev} onClick={onPrev}>
            <Icon name="chevron" size={14} className="st-pv-prev"/>
          </button>
          <button type="button" className="iconbtn" aria-label="Next file"
                  disabled={!hasNext} onClick={onNext}>
            <Icon name="chevron" size={14}/>
          </button>
          <button type="button" className="iconbtn" aria-label="Close preview" onClick={onClose}>
            <Icon name="close" size={14}/>
          </button>
        </div>
      </header>

      <div className="st-pv-body" ref={boxRef}>{body()}</div>

      <dl className="st-pv-facts">
        <div><dt>Size</dt><dd className="tnum">{fmtBytes(file.size)}</dd></div>
        <div><dt>Type</dt><dd title={file.mime_type}>{file.mime_type || 'unknown'}</dd></div>
        <div><dt>Added by</dt><dd>{file.owner_id === me?.id ? 'you' : file.owner_name || '—'}</dd></div>
        <div><dt>Added</dt><dd>{fmtDate(file.created_at)}</dd></div>
      </dl>

      <footer className="st-pv-actions">
        {draft !== null ? (
          <>
            <button type="button" className="btn btn--sm" onClick={save} disabled={state === 'saving'}>
              <Icon name="save" size={14}/> {state === 'saving' ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraft(null)}
                    disabled={state === 'saving'}>Cancel</button>
          </>
        ) : (
          <>
            <button type="button" className="btn btn--sm" onClick={() => onDownload(file)}>
              <Icon name="download" size={14}/> Download
            </button>
            {editable && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraft(text)}>
                <Icon name="edit" size={14}/> Edit
              </button>
            )}
            {(kind === 'video' || kind === 'audio') && onOpenTab && (
              <button type="button" className="btn btn--ghost btn--sm"
                      title="The same player, in its own tab, from where you left off"
                      onClick={() => onOpenTab(savedPosition(file.id))}>
                <Icon name="external" size={14}/> Open viewer in a new tab
              </button>
            )}
            <a className="btn btn--ghost btn--sm" href={src} target="_blank" rel="noreferrer">
              <Icon name="external" size={14}/> Open file
            </a>
          </>
        )}
      </footer>
    </section>
  );
}
